import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Zap, Crown, Home, Users, Trophy, Swords, Layers, UsersRound, Newspaper, ChevronRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GlobalSearch } from '@/components/GlobalSearch';
import { NotificationBell } from '@/components/NotificationBell';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import vsuLogo from '@/assets/vsu-logo.jpg';

const navLinks = [{
  href: '/',
  label: 'Home',
  icon: Home,
  gradient: 'from-blue-500 to-cyan-500'
}, {
  href: '/players',
  label: 'Players',
  icon: Users,
  gradient: 'from-cyan-500 to-blue-400'
}, {
  href: '/tournaments',
  label: 'Tournaments',
  icon: Trophy,
  gradient: 'from-amber-500 to-yellow-500'
}, {
  href: '/world-cup',
  label: 'World Cup',
  icon: Crown,
  gradient: 'from-yellow-500 to-amber-500',
  highlight: true
}, {
  href: '/matches',
  label: 'Matches',
  icon: Swords,
  gradient: 'from-blue-600 to-indigo-500'
}, {
  href: '/division',
  label: 'Division',
  icon: Layers,
  gradient: 'from-indigo-500 to-blue-500'
}, {
  href: '/squads',
  label: 'Squads',
  icon: UsersRound,
  gradient: 'from-sky-500 to-cyan-500'
}, {
  href: '/news',
  label: 'News',
  icon: Newspaper,
  gradient: 'from-cyan-500 to-teal-500'
}];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    user,
    isAdmin,
    isEditor,
    isHeadAdmin,
    signOut
  } = useAuth();
  const navigate = useNavigate();
  
  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full bg-background/95 backdrop-blur-xl border-b border-primary/30 shadow-lg shadow-primary/10">
      <div className="container flex h-14 md:h-16 items-center justify-between px-3 md:px-6">
        {/* Logo - Esports Pro Style */}
        <Link to="/" className="flex items-center gap-2 md:gap-3 group">
          <div className="relative">
            <img 
              src={vsuLogo} 
              alt="Viper Strike United Logo" 
              className="relative h-10 w-10 md:h-12 md:w-12 rounded-lg object-cover ring-2 ring-primary/50 group-hover:ring-primary group-hover:shadow-neon transition-all duration-300" 
            />
            <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-cyan-500 border-2 border-background animate-pulse" />
          </div>
          <div className="flex flex-col">
            <span className="font-gaming text-xl md:text-2xl font-black tracking-widest bg-gradient-to-r from-primary via-cyan-400 to-primary bg-clip-text text-transparent">
              VSU
            </span>
            <span className="hidden md:block text-[9px] uppercase tracking-[0.3em] font-bold bg-gradient-to-r from-cyan-400 via-primary to-blue-400 bg-clip-text text-transparent drop-shadow-sm animate-pulse-slow">
              Viper Strike United
            </span>
          </div>
        </Link>

        {/* Desktop Navigation - Esports Pro Style */}
        <nav className="hidden lg:flex items-center gap-0.5 bg-card/50 backdrop-blur-sm rounded-lg px-2 py-1.5 border border-primary/20">
          {navLinks.map(link => {
            const Icon = link.icon;
            return (
              <Link 
                key={link.href} 
                to={link.href} 
                className={cn(
                  "group relative px-4 py-2 text-sm font-medium transition-all rounded-md flex items-center gap-2",
                  link.highlight 
                    ? "text-amber-400 hover:bg-amber-500/10 hover:text-amber-300" 
                    : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                )}
              >
                <Icon className="h-4 w-4 opacity-70 group-hover:opacity-100 transition-opacity" />
                <span>{link.label}</span>
                {/* Active indicator */}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-primary to-cyan-500 group-hover:w-3/4 transition-all duration-300 rounded-full" />
              </Link>
            );
          })}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Global Search - hidden on mobile, shown in mobile menu */}
          <div className="hidden sm:block">
            <GlobalSearch />
          </div>
          
          {/* Notifications - always visible */}
          <NotificationBell />
          
          {/* Theme Toggle - hidden on mobile */}
          <div className="hidden sm:block">
            <ThemeToggle />
          </div>
          
          {/* Club Rules Button - visible for all users */}
          <Button asChild size="sm" className="hidden md:flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-500 hover:from-primary/90 hover:to-cyan-500/90 text-primary-foreground shadow-lg shadow-primary/25 rounded-md px-4">
            <Link to="/terms">
              <BookOpen className="h-4 w-4" />
              Club Rules
            </Link>
          </Button>

          {/* User Menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 md:h-10 md:w-10 rounded-md bg-gradient-to-br from-muted/50 to-muted border border-primary/20 hover:border-primary/50 hover:bg-primary/10 transition-all duration-200">
                  <User className="h-3.5 w-3.5 md:h-5 md:w-5 text-primary" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card/95 backdrop-blur-xl border-primary/20 rounded-lg">
                <DropdownMenuItem className="font-medium text-foreground">
                  {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border/50" />
              <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    My Profile
                  </Link>
                </DropdownMenuItem>
                {(isAdmin || isEditor || isHeadAdmin) && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      Admin Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/terms" className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    Club Rules
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border/50" />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" className="bg-gradient-to-r from-primary to-cyan-500 hover:from-primary/90 hover:to-cyan-500/90 text-primary-foreground shadow-sm shadow-primary/25 rounded-md px-1.5 md:px-4 h-5 md:h-9">
              <Link to="/auth" className="flex items-center gap-0.5">
                <Zap className="h-2 w-2 md:h-4 md:w-4" />
                <span className="text-[8px] md:text-sm font-semibold uppercase tracking-wider">Sign In</span>
              </Link>
            </Button>
          )}

          {/* Mobile Menu Button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden h-9 w-9 rounded-md bg-primary/80 hover:bg-primary border-0 text-white relative z-[60]" 
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }} 
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 top-14 z-50 bg-background animate-fade-in overflow-y-auto">
          <nav className="container flex flex-col gap-1.5 py-3 px-3">
            {/* Quick Actions - Search, Notifications, Theme */}
            <div className="flex items-center justify-end gap-1.5 mb-2 pb-2 border-b border-border/30">
              <div className="flex items-center gap-1">
                <GlobalSearch />
                <NotificationBell />
                <ThemeToggle />
              </div>
            </div>

            {/* Navigation Links */}
            {navLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <Link 
                  key={link.href} 
                  to={link.href} 
                  className="group flex items-center gap-2 px-2.5 py-2 rounded-lg bg-muted/30 border border-border/30 hover:border-primary/30 hover:bg-primary/5 transition-all duration-300"
                  style={{ animationDelay: `${index * 30}ms` }}
                  onClick={() => setIsOpen(false)}
                >
                  <div className={`flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br ${link.gradient} shadow-sm`}>
                    <Icon className="h-3 w-3 text-white" />
                  </div>
                  <span className="flex-1 text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                    {link.label}
                  </span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </Link>
              );
            })}

            {/* Admin Panel Link - only for admins */}
            {(isAdmin || isEditor || isHeadAdmin) && (
              <Link 
                to="/admin" 
                className="group flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-all duration-200 mt-1 bg-amber-500/10 border-amber-500/30 hover:border-amber-500/50"
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center justify-center w-6 h-6 rounded-md shadow-sm bg-gradient-to-br from-amber-500 to-yellow-500">
                  <Crown className="h-3 w-3 text-black" />
                </div>
                <span className="flex-1 text-xs font-semibold text-amber-500">
                  Admin Panel
                </span>
                <ChevronRight className="h-3 w-3 text-amber-500 group-hover:translate-x-1 transition-all" />
              </Link>
            )}

            {/* Club Rules Link */}
            <Link 
              to="/terms" 
              className="group flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-all duration-200 mt-1 bg-primary/5 border-primary/30 hover:border-primary/50"
              onClick={() => setIsOpen(false)}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-md shadow-sm bg-gradient-to-br from-primary to-cyan-500">
                <BookOpen className="h-3 w-3 text-white" />
              </div>
              <span className="flex-1 text-xs font-semibold text-primary">
                Club Rules
              </span>
              <ChevronRight className="h-3 w-3 text-primary group-hover:translate-x-1 transition-all" />
            </Link>

          </nav>
        </div>
      )}
    </header>
  );
}
